export function initialState() {
    return {
        me: null,
        schema: null,
        groups: [],
        connection: "closed",
        issues: {},
        pending: [],
        display: {},
        views: {},
        comments: {},
        activity: {},
        links: {},
        watchers: {},
        attachments: {},
        articleTick: null,
        boards: {},
        frozen: [],
        notifications: { items: [], unread: 0, loaded: false },
        savedSearches: [],
        drafts: [],
        keyIndex: {},
        errors: [],
    };
}
/**
 * Minimal external store: immutable snapshots + listeners, the contract
 * React's `useSyncExternalStore` expects. No framework dependency so the same
 * core runs in node for the end-to-end test.
 */
export class Store {
    state;
    listeners = new Set();
    constructor(state) {
        this.state = state;
    }
    /** Arrow properties so `store.get` / `store.subscribe` can be handed to useSyncExternalStore unbound. */
    get = () => this.state;
    set(update) {
        const next = update(this.state);
        if (next === this.state)
            return;
        this.state = next;
        for (const l of [...this.listeners])
            l();
    }
    subscribe = (listener) => {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    };
}
/** Server state with every pending patch for that issue applied in order. */
export function foldPending(server, pending, schema, now = Date.now()) {
    let out = server;
    for (const p of pending) {
        if (p.issueId !== server.id)
            continue;
        out = applyPatch(out, p.patch, schema, now);
    }
    return out;
}
/**
 * Same semantics as the server's updateIssue: keys in `fields` overwrite,
 * empty arrays clear, `updatedAt` moves to now, and `resolvedAt` follows the
 * State field (an element flagged `isResolved`). Without a schema the
 * resolved flag is left alone.
 */
export function applyPatch(issue, patch, schema = null, now = Date.now()) {
    let fields = issue.fields;
    let resolvedAt = issue.resolvedAt;
    if (patch.fields) {
        const next = { ...issue.fields };
        for (const [k, v] of Object.entries(patch.fields)) {
            if (v.length === 0)
                delete next[k];
            else
                next[k] = v;
        }
        fields = next;
        const state = schema ? stateFieldOf(schema, issue.projectId) : null;
        if (state) {
            const v = next[`f_${state.id}`]?.[0];
            const resolved = typeof v === "number" ? (schema.elementsById.get(v)?.isResolved ?? false) : false;
            resolvedAt = resolved ? (issue.resolvedAt ?? now) : null;
        }
    }
    return {
        ...issue,
        ...(patch.summary !== undefined ? { summary: patch.summary } : {}),
        ...(patch.description !== undefined ? { descriptionText: patch.description } : {}),
        ...(patch.visibility !== undefined ? { visibility: patch.visibility } : {}),
        ...(patch.tags !== undefined ? { tags: [...new Set(patch.tags)].sort((a, b) => a - b) } : {}),
        fields,
        resolvedAt,
        updatedAt: now,
    };
}
/** The State-kind field attached to a project, if any (the server derives resolved_at from it). */
export function stateFieldOf(schema, projectId) {
    for (const pf of schema.projectFieldsByProject.get(projectId) ?? []) {
        const def = schema.fieldsById.get(pf.fieldId);
        if (def?.kind === "state")
            return def;
    }
    return null;
}
/** Recompute `display` for `ids` from `issues` + `pending`. */
export function recomputeDisplay(s, ids) {
    const display = { ...s.display };
    let changed = false;
    for (const id of ids) {
        const server = s.issues[id];
        if (!server) {
            if (id in display) {
                delete display[id];
                changed = true;
            }
            continue;
        }
        const next = foldPending(server, s.pending, s.schema);
        if (display[id] !== next) {
            display[id] = next;
            changed = true;
        }
    }
    return changed ? { ...s, display } : s;
}
//# sourceMappingURL=store.js.map